import { Component, HostListener, Input, OnDestroy, OnInit, Renderer2, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthService } from "src/app/services";
import { CommonService } from "src/app/services/common.service";
import { UserService } from "src/app/services/user.service";
import { ToastService } from "src/app/services/toast.service";
import { EventService } from "src/app/services/event.service";
import { EventKeys } from "src/app/shared/constants/eventKeys.constants";

@Component({
  standalone: false,
  selector: "app-sidebar-layout",
  templateUrl: "./sidebar-layout.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./sidebar-layout.component.scss"]
})
export class SidebarLayoutComponent implements OnInit, OnDestroy {
  @Input() showMenuToggle = false;
  @Input() isDialog = false;
  @Input() fillHeight = false;
  collapsed: boolean;
  isPinned: boolean = false;
  isMobile: boolean = false;
  disablePointerEvents: boolean;
  private destroy$ = new Subject<void>();

  constructor(
    private renderer: Renderer2,
    private commonService: CommonService,
    private authService: AuthService,
    private router: Router,
    private userService: UserService,
    private toastService: ToastService,
    private eventService: EventService
  ) { }

  ngOnInit(): void {
    this.checkMobileView();
    this.loadPinnedState();

    if (this.isPinned && !this.isMobile) {
      this.collapsed = true;
    } else {
      this.collapsed = this.commonService.isSidenavCollapsed;
    }

    this.commonService.sidenavToggleEmitter.pipe(takeUntil(this.destroy$)).subscribe(() => this.toggleSidenav());
    this.updateHeaderPadding();
    this.eventService.on<boolean>(EventKeys.blockOutsideCLick).pipe(takeUntil(this.destroy$)).subscribe(isDisabled => {
      this.disablePointerEvents = isDisabled;
    })
  }

  @HostListener('window:resize')
  onResize() {
    this.checkMobileView();
  }

  checkMobileView() {
    this.isMobile = window.innerWidth < 768;
    if (this.isMobile && this.isPinned) {
      this.isPinned = false;
      this.updateHeaderPadding();
    }
  }

  loadPinnedState() {
    const currentUser = this.authService.getCurrentUser();
    this.isPinned = currentUser?.preferences?.isSidebarPinned || false;
  }

  togglePin(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.isPinned = !this.isPinned;
    this.authService.setCurrentUserSidebarPinned(this.isPinned);
    this.userService.patchUserPreferences({ isSidebarPinned: this.isPinned }).subscribe();

    if (this.isPinned) {
      this.collapsed = true;
    }

    this.updateHeaderPadding();
  }

  toggleSidenav() {
    if (this.isPinned) {
      this.toastService.showInfo('Cannot collapse while pinned', true);
      return;
    }
    this.collapsed = !this.collapsed;
    this.commonService.isSidenavCollapsed = this.collapsed;
    this.updateHeaderPadding();
  }

  updateHeaderPadding() {
    if (this.isPinned) {
      this.renderer.addClass(document.body, 'sidenav-pinned');
      this.renderer.removeClass(document.body, 'sidenav-open');
    } else if (this.collapsed) {
      this.renderer.addClass(document.body, 'sidenav-open');
      this.renderer.removeClass(document.body, 'sidenav-pinned');
    } else {
      this.renderer.removeClass(document.body, 'sidenav-open');
      this.renderer.removeClass(document.body, 'sidenav-pinned');
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  routeToLandingPage() {
    const url = this.authService.getLandingPageUrl();
    this.router.navigate([url]);
  }

  onOutsideClick(): void {
    if (this.isPinned) {
      return;
    }
    if (this.collapsed) {
      this.toggleSidenav();
    }
  }
}
